import React from 'react';
import { Database, MessageSquare, Zap, ArrowRight } from 'lucide-react';

const FeaturesSection = () => {
  const features = [
    {
      icon: Database,
      title: 'Turn your GTM chaos into a stream of clear actions',
      description: 'Forsyt connects Salesforce, Gong, email, and your note-taker to automatically update every field and surface a prioritized "what to do next" card for every rep, no exports, no interrogations, no guesswork. Leaders see the pipeline as it actually is. Reps know exactly where to focus.',
      bullets: ['No more pipeline hallucinations.'],
      image: '/product-risk-radar.webp',
    },
    {
      icon: MessageSquare,
      title: 'The operating system for revenue leaders who need clarity, not more charts',
      description: 'CROs and RevOps leaders use Forsyt to see stalled deals, diagnose real blockers, and act on recommended plays, all in one view, without the spreadsheets.',
      bullets: ['No more tool fatigue. No more spreadsheet backchannels.'],
      image: '/product-opportunities.webp',
    },
    {
      icon: Zap,
      title: 'Less admin. More selling.',
      description: 'Forsyt plugs into Salesforce, Zoho, and LeadSquared to capture actions automatically and surface one-click next steps, so your AEs stay in flow instead of fighting their CRM.',
      bullets: ['Automate revenue operations with measurable ROI or you don\'t pay.'],
      image: '/product-mail-intelligence.webp',
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="space-y-20">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isEven = index % 2 === 0;

            return (
              <div
                key={index}
                className={`flex flex-col lg:flex-row items-center gap-10 lg:gap-16 ${
                  isEven ? '' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className="w-full lg:flex-[1.3] max-w-xl space-y-5">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                    <Icon className="h-5 w-5 text-blue-600" />
                  </div>

                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-snug tracking-tight">
                    {feature.title}
                  </h2>

                  <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>

                  <ul className="space-y-2 pt-1">
                    {feature.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-center text-sm font-semibold text-gray-800">
                        <ArrowRight className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Screenshot Image */}
                <div className="w-full lg:flex-1 max-w-sm lg:max-w-md">
                  <div className="relative rounded-xl overflow-hidden shadow-xl border border-gray-200 group hover:shadow-2xl transition-all duration-500">
                    <img
                      src={feature.image}
                      alt={`Forsyt Revenue OS dashboard screenshot - ${feature.title}`}
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
